import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Share } from 'lucide-react';

// Install card for the software (app.moji.org.in). The public website's "Install App"
// button sends people here with ?install=1, because a phone can only install the site it is on.

let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach(fn => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    try { sessionStorage.removeItem('moji_install'); } catch (e) { /* ignore */ }
    listeners.forEach(fn => fn());
  });
  // Remember the request: logged-in users are redirected and the ?install=1 is lost
  try {
    if (new URLSearchParams(window.location.search).get('install') === '1') sessionStorage.setItem('moji_install', '1');
  } catch (e) { /* ignore */ }
}

const isStandalone = () => typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);
const isIos = () => typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);
const wanted = () => { try { return sessionStorage.getItem('moji_install') === '1'; } catch (e) { return false; } };

export default function AppInstallPrompt() {
  const [, setTick] = useState(0);
  const [closed, setClosed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const fn = () => setTick(t => t + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  // The public website (moji.org.in) is not installable; its button links here instead
  const host = window.location.hostname;
  if (host === 'moji.org.in' || host === 'www.moji.org.in') return null;
  if (closed || isStandalone()) return null;
  const want = wanted();
  const canPrompt = !!deferredPrompt;
  const onLogin = pathname === '/login';
  // Show when the visitor came to install, or on the login screen when the phone offers it
  if (!(want && (canPrompt || isIos())) && !(onLogin && canPrompt)) return null;

  const close = () => {
    try { sessionStorage.removeItem('moji_install'); } catch (e) { /* ignore */ }
    setClosed(true);
  };
  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch (e) { /* ignore */ }
    deferredPrompt = null;
    close();
  };

  return (
    <div className="md:hidden fixed inset-x-3 bottom-4 z-[100] rounded-2xl bg-[#241811] text-[#f4ece0] shadow-2xl p-4 flex items-start gap-3">
      <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl bg-white/10 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Install MOZI app</p>
        {canPrompt ? (
          <p className="text-xs opacity-80 mt-0.5">Open the software straight from your home screen.</p>
        ) : (
          <p className="text-xs opacity-80 mt-0.5 leading-relaxed">
            Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" /> <b>Share</b> in Safari, then <b>Add to Home Screen</b>.
          </p>
        )}
        {canPrompt && (
          <button onClick={install} className="mt-2.5 inline-flex items-center gap-1.5 bg-[#b5622f] text-white px-4 py-2 rounded-full text-xs font-semibold">
            <Download className="w-3.5 h-3.5" /> Install
          </button>
        )}
      </div>
      <button onClick={close} aria-label="Close" className="p-1 opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
    </div>
  );
}
